// Sprint 8 — Perfil público + book (/p y /book)
export type PublicProfileThemeId =
  | "classic"
  | "ocean"
  | "forest"
  | "sunset"
  | "midnight"
  | "lavender"
  | "coral"
  | "mint"
  | "honey";

export type PublicProfileThemeMeta = {
  id: PublicProfileThemeId;
  label: string;
  /** Tres muestras para la grilla de selección */
  swatches: [string, string, string];
  /** Fondo suave de la tarjeta en el picker */
  cardTint: string;
};

export const PROFILE_THEME_META: PublicProfileThemeMeta[] = [
  {
    id: "classic",
    label: "Clásico",
    swatches: ["#57534e", "#a8a29e", "#d6d3d1"],
    cardTint: "#fafaf9",
  },
  {
    id: "ocean",
    label: "Océano",
    swatches: ["#075985", "#0284c7", "#bae6fd"],
    cardTint: "#f0f9ff",
  },
  {
    id: "forest",
    label: "Bosque",
    swatches: ["#166534", "#22c55e", "#bbf7d0"],
    cardTint: "#f0fdf4",
  },
  {
    id: "sunset",
    label: "Atardecer",
    swatches: ["#c2410c", "#fb923c", "#ffedd5"],
    cardTint: "#fff7ed",
  },
  {
    id: "lavender",
    label: "Lavanda",
    swatches: ["#6b21a8", "#a855f7", "#e9d5ff"],
    cardTint: "#faf5ff",
  },
  {
    id: "midnight",
    label: "Medianoche",
    swatches: ["#1e293b", "#475569", "#cbd5e1"],
    cardTint: "#f1f5f9",
  },
  {
    id: "coral",
    label: "Coral",
    swatches: ["#be123c", "#fb7185", "#ffe4e6"],
    cardTint: "#fff1f2",
  },
  {
    id: "mint",
    label: "Menta",
    swatches: ["#0f766e", "#2dd4bf", "#ccfbf1"],
    cardTint: "#f0fdfa",
  },
  {
    id: "honey",
    label: "Miel",
    swatches: ["#b45309", "#f59e0b", "#fef3c7"],
    cardTint: "#fffbeb",
  },
];

export const PUBLIC_PROFILE_THEME_OPTIONS: { id: PublicProfileThemeId; label: string }[] = PROFILE_THEME_META.map(
  ({ id, label }) => ({ id, label }),
);

export function parsePublicProfileThemeId(raw: string | undefined | null): PublicProfileThemeId {
  const s = typeof raw === "string" ? raw : "classic";
  return PROFILE_THEME_META.some((m) => m.id === s) ? (s as PublicProfileThemeId) : "classic";
}

/** Variables CSS para el contenedor del perfil /p/[slug] y book /book/[slug]. */
export function publicProfileThemeStyleVars(theme: string): Record<string, string> {
  const t = parsePublicProfileThemeId(theme);
  const map: Record<PublicProfileThemeId, Record<string, string>> = {
    classic: {
      "--pp-page-bg": "#f5f5f4",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#1c1917",
      "--pp-muted": "#78716c",
      "--pp-border": "#e7e5e4",
    },
    ocean: {
      "--pp-page-bg": "#ecfeff",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#0c4a6e",
      "--pp-muted": "#0e7490",
      "--pp-border": "#a5f3fc",
    },
    forest: {
      "--pp-page-bg": "#f0fdf4",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#14532d",
      "--pp-muted": "#166534",
      "--pp-border": "#bbf7d0",
    },
    sunset: {
      "--pp-page-bg": "#fff7ed",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#7c2d12",
      "--pp-muted": "#c2410c",
      "--pp-border": "#fed7aa",
    },
    midnight: {
      "--pp-page-bg": "#0f172a",
      "--pp-card-bg": "#1e293b",
      "--pp-text": "#f8fafc",
      "--pp-muted": "#94a3b8",
      "--pp-border": "#334155",
    },
    lavender: {
      "--pp-page-bg": "#faf5ff",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#4c1d95",
      "--pp-muted": "#7c3aed",
      "--pp-border": "#e9d5ff",
    },
    coral: {
      "--pp-page-bg": "#fff1f2",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#9f1239",
      "--pp-muted": "#e11d48",
      "--pp-border": "#fecdd3",
    },
    mint: {
      "--pp-page-bg": "#f0fdfa",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#115e59",
      "--pp-muted": "#0d9488",
      "--pp-border": "#99f6e4",
    },
    honey: {
      "--pp-page-bg": "#fffbeb",
      "--pp-card-bg": "#ffffff",
      "--pp-text": "#78350f",
      "--pp-muted": "#b45309",
      "--pp-border": "#fde68a",
    },
  };
  return map[t];
}
